import { Accordion } from '@Components/common/accordion'
import {
  AlertDialog,
  Badge,
  Button,
  Card,
  Container,
  DropdownMenu,
  Flex,
  Heading,
  IconButton,
  Link,
  Separator,
  Text,
  TextField,
} from '@radix-ui/themes'
import { Accordion as RadixAccordion } from 'radix-ui'
import './style.scss'
import { useMemo, useState } from 'react'
import { WithPermissions } from '@Components/common/with-permissions'
import {
  AdminSearchConfiguration,
  AuthScopes,
  SearchCollection,
  SearchOverride,
} from '@Generated/back-office-client/src'
import { UpsertSearchRule } from './upsert-search-rule'
import { CreateSearchConfig } from './create-search-config'
import { Cross2Icon, TrashIcon } from '@radix-ui/react-icons'

export type SearchConfigurationViewProps = {
  searchCollections: SearchCollection[] | undefined
  currentCollection: SearchCollection | null
  onSelectCollection: (collection: SearchCollection) => void

  adminSearchConfig: AdminSearchConfiguration | undefined
  onSaveAdminConfig: (config: AdminSearchConfiguration) => void

  overrides: SearchOverride[] | undefined
  onDeleteOverride: (overrideName: string) => void
  onUpsertOverrideSuccess: () => void

  onCreated: (hostname: string) => void
}

export const SearchConfigurationView = ({
  searchCollections,
  currentCollection,
  onSelectCollection,
  adminSearchConfig,
  onSaveAdminConfig,
  overrides,
  onDeleteOverride,
  onUpsertOverrideSuccess,
  onCreated,
}: SearchConfigurationViewProps) => {
  const [openOverrides, setOpenOverrides] = useState<string[]>([])
  const [showAddNewRule, setShowAddNewRule] = useState(false)
  const [newNoResultMessage, setNewNoResultMessage] = useState<string>('')
  const [showCreate, setShowCreate] = useState(false)

  const existingOverrides = useMemo(() => {
    if (!(overrides && currentCollection)) {
      return null
    }

    return overrides.map((override) => {
      const overrideIsOpen = openOverrides.find((o) => o === override.name)
      const child = (
        <RadixAccordion.Content className="AccordionContent" key={override.name}>
          {overrideIsOpen ? (
            <UpsertSearchRule
              existingOverride={override}
              currentCollection={currentCollection}
              onClose={() => {
                setOpenOverrides(openOverrides.filter((o) => o !== override.name))
              }}
              onSubmit={() => {
                setOpenOverrides(openOverrides.filter((o) => o !== override.name))
                onUpsertOverrideSuccess()
              }}
            />
          ) : null}
        </RadixAccordion.Content>
      )

      const actionButton = (
        <AlertDialog.Root>
          <AlertDialog.Trigger onClick={(e) => e.stopPropagation()}>
            <TrashIcon className="delete-override" />
          </AlertDialog.Trigger>
          <AlertDialog.Content maxWidth="500px" style={{ paddingBottom: '60px' }}>
            <AlertDialog.Cancel>
              <Flex justify="end">
                <IconButton variant="ghost" aria-label="Cancel">
                  <Cross2Icon height="20" width="20" style={{ color: 'black' }} />
                </IconButton>
              </Flex>
            </AlertDialog.Cancel>
            <AlertDialog.Title align="center" style={{ paddingTop: '29px' }}>
              Are you sure?
            </AlertDialog.Title>
            <AlertDialog.Description size="2" style={{ paddingBottom: '18px' }} align="center">
              {override.name} will be permanently deleted and must be recreated if desired.
            </AlertDialog.Description>

            <Flex gap="3" justify="center">
              <AlertDialog.Action
                onClick={() => {
                  onDeleteOverride(override.name)
                }}>
                <Button color="red">Delete</Button>
              </AlertDialog.Action>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>
      )

      return { id: override.name, value: override.name, name: override.name, child, actionButton }
    })
  }, [currentCollection, onDeleteOverride, openOverrides, overrides, onUpsertOverrideSuccess])

  const collectionOptions = useMemo(() => {
    if (!searchCollections) {
      return null
    }

    return searchCollections.map((collection) => {
      return (
        <DropdownMenu.Item key={collection.hostname} onClick={() => onSelectCollection(collection)}>
          {collection.name}
        </DropdownMenu.Item>
      )
    })
  }, [searchCollections, onSelectCollection])

  const collectionSelector = (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <Button variant="soft" style={{ cursor: 'pointer' }}>
          {currentCollection?.name ?? 'Select a configuration'}
          <DropdownMenu.TriggerIcon />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>{collectionOptions}</DropdownMenu.Content>
    </DropdownMenu.Root>
  )

  return (
    <Container>
      <Flex className="search-configuration-header" justify="between" align="center">
        <Heading>Search Configurations</Heading>
        <Flex gap="3" align="center">
          {searchCollections?.length ? collectionSelector : null}
          <WithPermissions requiredPermissions={AuthScopes.writesearch}>
            <Button style={{ cursor: 'pointer' }} onClick={() => setShowCreate(true)}>
              Create
            </Button>
          </WithPermissions>
        </Flex>
      </Flex>

      {currentCollection ? (
        <Heading as="h2" size="5" mt="4">
          View / Edit Configuration: {currentCollection.name}
        </Heading>
      ) : null}

      {searchCollections !== undefined && !currentCollection ? (
        <Card mt="4">
          <Flex align="center" justify="center" style={{ minHeight: '160px', padding: '24px' }}>
            <Text size="3" color="gray">
              Select a search configuration
              <WithPermissions requiredPermissions={AuthScopes.writesearch}>
                {' or '}
                <Link onClick={() => setShowCreate(true)} style={{ cursor: 'pointer' }}>
                  create a new one
                </Link>
              </WithPermissions>
              .
            </Text>
          </Flex>
        </Card>
      ) : null}

      {currentCollection ? (
        <>
          <Card className="no-results-messages" mt="4">
            <Heading as="h3" size="4">
              No Results Messages
            </Heading>

            <WithPermissions requiredPermissions={AuthScopes.writesearch}>
              <TextField.Root
                placeholder="No results message..."
                className="no-results-message-input"
                value={newNoResultMessage}
                onChange={(e) => setNewNoResultMessage(e.target.value)}>
                <TextField.Slot></TextField.Slot>
                <Button
                  onClick={() => {
                    onSaveAdminConfig({
                      noResultMessages: [
                        ...(adminSearchConfig?.noResultMessages ?? []),
                        newNoResultMessage,
                      ],
                    })
                    setNewNoResultMessage('')
                  }}>
                  Add
                </Button>
              </TextField.Root>
            </WithPermissions>

            <Flex gap="2" wrap="wrap">
              {adminSearchConfig?.noResultMessages.map((message) => {
                return (
                  <Badge color="gray" key={message}>
                    {message}
                    <Button
                      color="red"
                      onClick={() => {
                        onSaveAdminConfig({
                          noResultMessages: adminSearchConfig?.noResultMessages.filter(
                            (m) => m !== message,
                          ),
                        })
                      }}>
                      X
                    </Button>
                  </Badge>
                )
              })}
            </Flex>
          </Card>

          <Card mt="4">
            <Flex justify="between" align="center" mb="3">
              <Heading as="h3" size="4">
                Pinned Results Rules
              </Heading>
              <WithPermissions requiredPermissions={AuthScopes.writesearch}>
                <Button
                  style={{ cursor: 'pointer' }}
                  onClick={() => setShowAddNewRule(true)}
                  disabled={showAddNewRule}>
                  Add new rule
                </Button>
              </WithPermissions>
            </Flex>

            {showAddNewRule ? (
              <UpsertSearchRule
                currentCollection={currentCollection}
                onClose={() => setShowAddNewRule(false)}
                onSubmit={() => {
                  setShowAddNewRule(false)
                  onUpsertOverrideSuccess()
                }}
              />
            ) : null}

            {overrides ? (
              <Flex className="existing-rule-container" direction="column">
                <Separator size="4" className="existing-rule-container__separator" />
                {existingOverrides ? (
                  <Accordion
                    items={existingOverrides}
                    values={openOverrides}
                    onValueChange={setOpenOverrides}
                  />
                ) : null}
              </Flex>
            ) : null}
          </Card>
        </>
      ) : null}

      {showCreate ? (
        <CreateSearchConfig onClose={() => setShowCreate(false)} onCreated={onCreated} />
      ) : null}
    </Container>
  )
}
